import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Bookmark } from 'lucide-react';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNavigate } from 'react-router-dom';
import { PLANBASE_CONFIG } from '../config';
import { getAuthHeaders } from '../utils/auth';
import { ProjectCard } from '../components/ProjectCard';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

// Component to display a bookmarked project card
function BookmarkedProjectCard({ bookmark, onClick }: { bookmark: any; onClick: () => void }) {
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const projectId = bookmark.slug.replace('project_', '');

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchProject = async () => {
      try {
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/cms/records/${projectId}?database_slug=projects`,
          { headers: getAuthHeaders(true) }
        );
        if (response.ok) {
          const data = await response.json();
          setProject(data.record);
        } else {
          setProject({
            id: projectId,
            data: {
              title: 'Project Not Found',
              description: 'This project may have been deleted or is no longer available.',
              coverImage: null,
              category: bookmark.collection_name,
            },
          });
        }
      } catch {
        setProject({
          id: projectId,
          data: {
            title: 'Error Loading Project',
            description: 'Unable to load project details.',
            coverImage: null,
            category: bookmark.collection_name,
          },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId, bookmark.collection_name]);

  if (loading) {
    return (
      <div className="border border-border rounded-2xl overflow-hidden">
        <div className="aspect-[4/3] bg-muted animate-pulse" />
        <div className="p-3 space-y-2">
          <div className="h-4 bg-muted rounded animate-pulse" />
          <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <Card className="border-border overflow-hidden">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground text-sm">Project not found</p>
        </CardContent>
      </Card>
    );
  }

  return <ProjectCard project={project} onClick={onClick} />;
}

export default function Collections() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [selectedCollection, setSelectedCollection] = useState<string>('all');
  const { bookmarks, loading, error } = useBookmarks(
    selectedCollection === 'all' ? undefined : selectedCollection
  );

  const collectionGroups = bookmarks.reduce((acc, bookmark) => {
    const collection = bookmark.collection_name || 'default';
    if (!acc[collection]) acc[collection] = [];
    acc[collection].push(bookmark);
    return acc;
  }, {} as Record<string, typeof bookmarks>);

  const collections = Object.keys(collectionGroups);

  // --- Empty state ---
  if (!loading && !error && bookmarks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Collections</h1>
        </div>
        <Card className="border-border">
          <CardContent className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-50 dark:bg-orange-950 mb-4">
              <Bookmark className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-base font-medium text-foreground mb-2">No Bookmarks Yet</h3>
            <p className="text-muted-foreground mb-4">Start bookmarking projects to build your collection</p>
            <button
              onClick={() => navigate('/discover')}
              className="inline-flex items-center px-4 h-8 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
            >
              Discover Projects
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filterPills = (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      <button
        onClick={() => setSelectedCollection('all')}
        className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors ${
          selectedCollection === 'all'
            ? 'bg-orange-500 text-white'
            : 'bg-muted text-muted-foreground hover:text-foreground'
        }`}
      >
        All ({bookmarks.length})
      </button>
      {collections.map((collection) => (
        <button
          key={collection}
          onClick={() => setSelectedCollection(collection)}
          className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors capitalize ${
            selectedCollection === collection
              ? 'bg-orange-500 text-white'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          {collection} ({collectionGroups[collection]?.length ?? 0})
        </button>
      ))}
    </div>
  );

  const gridContent = loading ? (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="border border-border rounded-2xl overflow-hidden">
          <div className="aspect-[4/3] bg-muted animate-pulse" />
          <div className="p-3 space-y-2">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
          </div>
        </div>
      ))}
    </div>
  ) : error ? (
    <div className="text-center py-12">
      <p className="text-muted-foreground text-sm">{error}</p>
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {bookmarks.map((bookmark) => {
        const projectId = bookmark.slug.replace('project_', '');
        return (
          <BookmarkedProjectCard
            key={bookmark.id}
            bookmark={bookmark}
            onClick={() => navigate(`/project/${projectId}`)}
          />
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Collections</h1>
      </div>

      {isDesktop ? (
        <div className="bg-white dark:bg-[#0a0a0a] rounded-3xl overflow-hidden border border-border">
          <div className="sticky top-0 z-10 bg-[#f0f0f0] dark:bg-muted/40 border-b border-border px-4 py-3">
            {filterPills}
          </div>
          <div className="p-4">
            {gridContent}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filterPills}
          {gridContent}
        </div>
      )}
    </div>
  );
}
